// The entity namespaces server-ops composes over the generated client.
//
// Q-10 (HS-939) landed: isolation moved off Tenant onto institution/group.
// The `group` namespace now exists (Institution → Group → Lab → Device), and
// composite user/scope/permission keys route through `{institutionId}`.
//
// HS-1382: `AssignmentController` (the ADR-0001 aggregate `assignment` named, HS-847 P0.3) is
// retired outright — not merely deprecated, the backend removed it. `UserRoleController`
// replaces it as `userRole`. Scopes and permissions no longer hang directly off `user`; they hang
// off a specific `userRole` (a role assignment), which is why `scope`/`permission` are re-parented
// one level deeper below.

export const ENTITY_NAMES = [
  'device',
  'user',
  'lab',
  'group',
  'institution',
  'certification',
  'certificationProgram',
  'rfidTag',
  'stream',
  'role',
  'userRole',
  'scope',
  'permission',
  'log',
  // HS-1548: an administrator's decision on a service request. Own root, no parent: a request
  // names an institution and a requester, but deciding it changes neither's cached tree. The one
  // exception — completing an AccessExtension moves the requester's access end — is invalidated
  // by the caller with `invalidations.user`, because it depends on the request's type.
  'serviceRequest',
  // HS-1659: the admin's report runs and schedules (`/api/reports/*`). Own root, no parent: a run
  // or a schedule names an institution and a laboratory but changes neither's cached tree. The
  // Reports section keeps its own `['reports', …]` cache and refreshes it in its hooks.
  'report',
] as const;

export type EntityName = (typeof ENTITY_NAMES)[number];

/**
 * Parent in the containment hierarchy, for invalidation cascades. Chain:
 * institution → group → lab → device; user hangs off institution, userRole off user, and
 * scope/permission off the specific userRole (role assignment) they belong to.
 */
export const parentOf: Partial<Record<EntityName, EntityName>> = {
  device: 'lab',
  // A certification programme belongs to exactly one laboratory, so editing it refreshes that
  // lab's tree and everything above it. `certification` — the per-user record — deliberately has
  // no parent here: it hangs off an institution and a user, not off the programme.
  certificationProgram: 'lab',
  lab: 'group',
  group: 'institution',
  user: 'institution',
  userRole: 'user',
  scope: 'userRole',
  permission: 'userRole',
};

/** Walk the parent chain (excluding the entity itself), nearest ancestor first. */
export function ancestorsOf(entity: EntityName): EntityName[] {
  const chain: EntityName[] = [];
  let current = parentOf[entity];
  while (current) {
    chain.push(current);
    current = parentOf[current];
  }
  return chain;
}
